import { useFragment } from "@/generated";
import { GetProjectByParentIdDocument, ProjectFieldsFragmentDoc } from "@/generated/graphql";
import {
  type ProjectsByParentOptions,
  getProjectsByParentIdVars,
} from "@/lib/utils/graphql/getProjectsByParentIdVars";
import { useQuery } from "@apollo/client/react";

/**
 * Fetches and paginates projects belonging to a specific parent project.
 *
 * Queries the GraphQL API for projects under a parent project with support for
 * infinite scrolling/pagination, search, and sorting. Results are cached (cache-first)
 * for performance. The loadMore function appends additional pages to the existing list.
 *
 * @param options - Configuration options for the hook
 * @param options.parentId - The unique identifier of the parent project
 * @param options.searchText - Optional search query to filter projects by name
 * @param options.sortBy - Optional sort order (e.g., "ALPHABETICAL" or default to most recent)
 * @param options.skip - If true, skips the query execution (default: false)
 * @returns An object containing:
 *   - projects: Array of Project objects for the current page(s)
 *   - hasMore: Boolean indicating if more projects are available to load
 *   - loading: Boolean indicating if the initial query is in progress
 *   - loadMore: Function to fetch the next page of projects
 *
 * @example
 * const { projects, hasMore, loading, loadMore } = useProjectsByParent({
 *   parentId: "parent123",
 *   searchText: "science"
 * });
 */
export function useProjectsByParent({
  parentId,
  searchText,
  sortBy,
  isTrash = false,
  skip = false,
}: ProjectsByParentOptions) {
  const {
    data: projectsByParentIdData,
    loading,
    fetchMore,
    refetch,
    error,
  } = useQuery(GetProjectByParentIdDocument, {
    variables: getProjectsByParentIdVars({
      parentId,
      searchText,
      sortBy,
      isTrash,
    }),
    skip,
    fetchPolicy: "cache-and-network",
  });

  const maskedItems = projectsByParentIdData?.projectsByParentId.items ?? [];
  const items = maskedItems
    .map((project) => useFragment(ProjectFieldsFragmentDoc, project))
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const hasMore = projectsByParentIdData?.projectsByParentId.hasMore ?? false;

  const loadMore = () => {
    fetchMore({
      variables: {
        ...getProjectsByParentIdVars({
          parentId,
          searchText,
          sortBy,
          isTrash,
        }),
        offset: maskedItems.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const existingIds = new Set(
          useFragment(ProjectFieldsFragmentDoc, prev.projectsByParentId.items).map((p) => p.id)
        );
        const newItems = fetchMoreResult.projectsByParentId.items.filter(
          (item) => !existingIds.has(useFragment(ProjectFieldsFragmentDoc, item).id)
        );

        const unorderedItems = [...prev.projectsByParentId.items, ...newItems];

        const orderedItems = useFragment(ProjectFieldsFragmentDoc, unorderedItems).sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );

        return {
          projectsByParentId: {
            ...fetchMoreResult.projectsByParentId,
            items: orderedItems,
          },
        };
      },
    });
  };

  return {
    projects: items,
    hasMore,
    loading,
    error,
    refetch,
    loadMore,
  };
}
